import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import styles from './SuperAdminSettings.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authService from '../../services/authService';
import { API_BASE_URL } from '../../utils/constants';

const SuperAdminSettings: React.FC = () => {
  const { auth } = useAuth();
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [totpQr, setTotpQr] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);

  useEffect(() => {
    const fetchTotpStatus = async () => {
      try {
        const { enabled } = await authService.get2FAStatus() as { enabled: boolean };
        setTotpEnabled(enabled);
      } catch {
        setTotpEnabled(false);
      }
    };
    fetchTotpStatus();
  }, []);

  const handleTfaToggle = (checked: boolean) => {
    if (checked) {
      handleEnableTotp();
    } else {
      handleDisableTotp();
    }
  };

  const handleEnableTotp = async () => {
    setTotpLoading(true);
    try {
      const { qr, secret } = await authService.setup2FA() as { qr: string; secret: string };
      setTotpQr(qr);
      setTotpSecret(secret);
      setShowTotpSetup(true);
    } catch (e) {
      console.log("response::::::", e);
      toast.error('Failed to start 2FA setup.');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerifyTotp = async () => {
    setTotpLoading(true);
    try {
      await authService.verify2FA(totpCode);
      setTotpEnabled(true);
      setShowTotpSetup(false);
      setTotpCode('');
      setTotpQr('');
      setTotpSecret('');
      toast.success('Google Authenticator enabled!');
    } catch (e) {
      console.error('2FA verification failed:', e);
      // Check if it might be a time sync issue
      const now = new Date();
      const timeOffset = Math.abs(now.getTime() - Date.now());
      
      if (timeOffset > 30000) { // 30 seconds
        toast.error('Invalid code. Please check your device time is synchronized with the internet.');
      } else {
        toast.error('Invalid code. Please try again or check your device time is correct.');
      }
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisableTotp = async () => {
    setTotpLoading(true);
    try {
      await authService.disable2FA();
      setTotpEnabled(false);
      toast.success('Google Authenticator disabled.');
    } catch (e) {
      toast.error('Failed to disable 2FA.');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/backup`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Backup failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
              a.download = `database_backup_${new Date().toLocaleDateString('en-CA').replace(/\//g, '-')}.sql`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Database backup generated successfully!');
    } catch (error) {
      console.error('Error generating backup:', error);
      toast.error('Failed to generate backup');
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2>Settings</h2>
      
      {/* Database Backup Section */}
      <div className={styles.card}>
        <h2>Database Backup</h2>
        <div className={styles.backupSection}>
          <div className={styles.backupInfo}>
            <p>Generate a complete SQL backup of all database tables and data.</p>
            <p className={styles.backupDescription}>
              This will create a SQL file containing all table structures and data that can be used to restore the database.
            </p>
          </div>
          <div className={styles.backupActions}>
            <Button
              variant="primary"
              onClick={handleBackup}
              loading={backupLoading}
              disabled={backupLoading}
            >
              {backupLoading ? 'Generating Backup...' : 'Generate Backup'}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Google Authenticator (TOTP) Section */}
      <div className={styles.card}>
        <h2>Two-Factor Authentication (TFA)</h2>
        <div className={styles.tfaSection}>
          <div
            className={styles.tfaToggle}
            role="button"
            tabIndex={0}
            aria-pressed={totpEnabled}
            onClick={() => handleTfaToggle(!totpEnabled)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleTfaToggle(!totpEnabled);
            }}
            style={{ outline: 'none' }}
          >
            <div className={totpEnabled ? styles.toggleOn : styles.toggleOff}>
              <div className={styles.toggleKnob} />
            </div>
            <span className={styles.toggleLabel}>
              {totpEnabled ? 'TFA Enabled' : 'TFA Enable'}
            </span>
          </div>
          <div className={styles.tfaStatusMsg}>
            {totpEnabled
              ? 'Google Authenticator is enabled for your account.'
              : 'Protect your account with Google Authenticator.'}
          </div>
        </div>
      </div>

      <Modal isOpen={showTotpSetup} onClose={() => setShowTotpSetup(false)} title="Set up Google Authenticator" size='xl'>
        <div className={styles.tfaEnableForm}>
          <p>1. Scan this QR code with your Google Authenticator app:</p>
          {totpQr && <img src={totpQr} alt="TOTP QR Code" style={{ width: 200, height: 200 }} />}
          <p>Or enter this secret manually: <b>{totpSecret}</b></p>
          <p>2. Enter the 6-digit code from your app:</p>
          <input
            type="text"
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            maxLength={6}
            disabled={totpLoading}
          />
        </div>
        <div className={styles.modalActions}>
          <Button variant="primary" onClick={handleVerifyTotp} disabled={totpLoading || totpCode.length !== 6}>
            {totpLoading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
          <Button variant="secondary" onClick={() => setShowTotpSetup(false)} disabled={totpLoading}>
            Cancel
          </Button>
        </div>
      </Modal>
      
      <ToastContainer />
    </div>
  );
};

export default SuperAdminSettings;